import { BrandLogo, Button, cn } from '@giyus/ui';
import {
  ActivityIcon,
  BellIcon,
  BoxesIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FileTextIcon,
  HomeIcon,
  InboxIcon,
  LandmarkIcon,
  ListTodoIcon,
  SettingsIcon,
  ShieldCheckIcon,
  ScrollTextIcon,
  TruckIcon,
  UsersIcon,
  WrenchIcon,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { useContextStore } from '../lib/context/contextStore';
import { useActiveContext } from '../lib/context/useActiveContext';
import { usePermissions } from '../lib/permissions/usePermission';
import { buildNavItems } from './navItems';
import { activeSidebarKey, buildSidebarGroups, destinationMatches } from './sidebarNavigation';

export interface SidebarProps {
  readonly open: boolean;
  readonly drawer?: boolean;
  readonly onNavigate?: () => void;
}

const NAV_ICONS = {
  home: HomeIcon,
  'shared-mailboxes': InboxIcon,
  'research-centers': LandmarkIcon,
  studies: FileTextIcon,
  procedures: ActivityIcon,
  'participant-reimbursements': TruckIcon,
  suppliers: TruckIcon,
  people: UsersIcon,
  facilities: BoxesIcon,
  equipment: WrenchIcon,
  scheduling: CalendarDaysIcon,
  identity: UsersIcon,
  authz: ShieldCheckIcon,
  governance: ScrollTextIcon,
  documents: FileTextIcon,
  finance: LandmarkIcon,
  accounting: ScrollTextIcon,
  settings: SettingsIcon,
  'platform-health': ActivityIcon,
  workflow: ListTodoIcon,
  notifications: BellIcon,
};

const PINNED_SIDEBAR_STORAGE_KEY = 'giyus.sidebar.pinnedMenu';

function normalizeModuleFamily(to: string): string {
  const cleanPath = (to.split('?')[0] ?? '').replace(/\/+$/, '');
  const parts = cleanPath.split('/').filter(Boolean);
  if (parts[0] === 'network' && parts[1]) {
    if (parts[1] === 'research-centers') return '/network/people';
    if (parts[1] === 'equipment') return '/network/facilities';
    if (parts[1] === 'governance') return '/network/authz';
    return `/network/${parts[1]}`;
  }
  if (parts[0] === 't' && parts[1] && parts[2] === 's' && parts[3] && parts[4]) {
    const mod =
      parts[4] === 'equipment'
        ? 'facilities'
        : parts[4] === 'governance'
          ? 'authz'
          : parts[4];
    return `/t/${parts[1]}/s/${parts[3]}/${mod}`;
  }
  if (parts[0] === 't' && parts[1] && parts[2]) {
    const mod =
      parts[2] === 'equipment'
        ? 'facilities'
        : parts[2] === 'governance'
          ? 'authz'
          : parts[2];
    return `/t/${parts[1]}/${mod}`;
  }
  return cleanPath || '/';
}

export function Sidebar({ open, drawer = false, onNavigate }: SidebarProps) {
  const { t } = useTranslation(['shell', 'common']);
  const location = useLocation();
  const tenantCode = useContextStore((s) => s.activeTenantCode);
  const { context, isNetworkScope } = useActiveContext();
  const effectiveTenantCode = context?.code ?? tenantCode;
  const permissions = usePermissions();
  const items = buildNavItems(
    isNetworkScope ? 'network' : 'tenant',
    effectiveTenantCode,
    permissions,
  );
  const groups = buildSidebarGroups(items, isNetworkScope, permissions);
  const resolvedKey = activeSidebarKey(groups, location.pathname, location.search);
  const currentFamily = normalizeModuleFamily(location.pathname);
  const [pinnedMenu, setPinnedMenu] = useState<{
    readonly key: string;
    readonly family: string;
    readonly fromPathname: string;
  } | null>(() => {
    try {
      const raw = sessionStorage.getItem(PINNED_SIDEBAR_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { key?: unknown; family?: unknown };
        if (typeof parsed.key === 'string' && typeof parsed.family === 'string') {
          return { key: parsed.key, family: parsed.family, fromPathname: location.pathname };
        }
      }
    } catch {
      // ignore storage errors
    }
    return resolvedKey
      ? { key: resolvedKey, family: currentFamily, fromPathname: location.pathname }
      : null;
  });

  const [trackedFamily, setTrackedFamily] = useState<{
    readonly family: string;
    readonly key: string | undefined;
  }>(() => ({
    family: currentFamily,
    key:
      pinnedMenu && pinnedMenu.family === currentFamily && groups.some((g) => g.key === pinnedMenu.key)
        ? pinnedMenu.key
        : resolvedKey,
  }));

  if (trackedFamily.family !== currentFamily) {
    const nextKey =
      pinnedMenu &&
      pinnedMenu.family === currentFamily &&
      groups.some((g) => g.key === pinnedMenu.key)
        ? pinnedMenu.key
        : resolvedKey;
    setTrackedFamily({ family: currentFamily, key: nextKey });
  }

  const selectSidebarMenu = (key: string, to: string) => {
    const targetFamily = normalizeModuleFamily(to);
    const next = { key, family: targetFamily, fromPathname: location.pathname };
    setPinnedMenu(next);
    setTrackedFamily({ family: targetFamily, key });
    try {
      sessionStorage.setItem(
        PINNED_SIDEBAR_STORAGE_KEY,
        JSON.stringify({ key, family: targetFamily }),
      );
    } catch {
      // ignore storage errors
    }
    onNavigate?.();
  };

  const activeKey =
    pinnedMenu &&
    (pinnedMenu.family === currentFamily || pinnedMenu.fromPathname === location.pathname) &&
    groups.some((g) => g.key === pinnedMenu.key)
      ? pinnedMenu.key
      : trackedFamily.key && groups.some((g) => g.key === trackedFamily.key)
        ? trackedFamily.key
        : resolvedKey;
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});

  if (items.length === 0) return null;
  const homeHref =
    items[0]?.to ??
    (isNetworkScope
      ? '/network'
      : effectiveTenantCode
        ? `/t/${encodeURIComponent(effectiveTenantCode)}`
        : '/');
  const contextLabel = isNetworkScope
    ? t('shell:nav.networkContext')
    : t('shell:nav.centerContext', { name: context?.name ?? effectiveTenantCode ?? '' });

  const mainGroups = groups.filter((item) => item.key !== 'settings');
  const settingsGroup = groups.find((item) => item.key === 'settings');

  const renderGroup = (item: (typeof groups)[number]) => {
    const isActive = item.key === activeKey;
    const hasSubmenus = item.children.length > 0;
    const isExpanded = expandedModules[item.key] ?? false;
    const Icon = NAV_ICONS[item.key as keyof typeof NAV_ICONS] ?? ActivityIcon;
    const label = t(`shell:${item.labelKey}`);
    const submenuId = `${drawer ? 'mobile-' : ''}submenu-${item.key}`;
    return (
      <li key={item.key}>
        <div className={cn('giyus-nav-row', isActive && 'giyus-nav-row-active')}>
          <Link
            to={item.to}
            aria-current={isActive ? 'page' : undefined}
            onClick={() => {
              selectSidebarMenu(item.key, item.to);
            }}
            className="giyus-nav-link"
            data-testid={`nav-${item.key}`}
          >
            <Icon className="size-4 shrink-0" aria-hidden="true" />
            <span className="flex-1 truncate">{label}</span>
            {item.key === 'settings' && (
              <span className="ml-auto shrink-0 text-caption font-normal text-muted-foreground">
                {t('shell:nav.portal.systemBadge')}
              </span>
            )}
          </Link>
          {hasSubmenus && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setExpandedModules((prev) => ({ ...prev, [item.key]: !isExpanded }));
              }}
              aria-label={t('shell:nav.toggleSubmenu', { module: label })}
              aria-expanded={isExpanded}
              aria-controls={submenuId}
              className="size-8 shrink-0 self-center text-muted-foreground opacity-60 hover:opacity-100"
              data-testid={`nav-toggle-${item.key}`}
            >
              {isExpanded ? (
                <ChevronDownIcon aria-hidden="true" />
              ) : (
                <ChevronRightIcon aria-hidden="true" />
              )}
            </Button>
          )}
        </div>
        {hasSubmenus && (
          <ul
            id={submenuId}
            hidden={!isExpanded}
            className="giyus-nav-group-children"
            data-testid={`nav-submenus-${item.key}`}
          >
            {item.children.map((sub) => {
              const active = destinationMatches(sub.to, location.pathname, location.search, true);
              return (
                <li key={sub.id}>
                  <Link
                    to={sub.to}
                    onClick={() => {
                      selectSidebarMenu(item.key, sub.to);
                    }}
                    aria-current={active ? 'page' : undefined}
                    className={cn('giyus-nav-item', active && 'giyus-nav-item-active')}
                    data-testid={
                      sub.id === 'procurement' || sub.id === 'accounting'
                        ? `nav-${sub.id}`
                        : `nav-sub-${sub.id}`
                    }
                  >
                    {t(`shell:${sub.labelKey}`)}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </li>
    );
  };

  return (
    <nav
      id={drawer ? 'mobile-sidebar' : 'sidebar'}
      aria-label={t('common:a11y.mainNavigation')}
      className={cn(
        'giyus-nav-rail flex-col gap-4 px-3.5 py-5',
        drawer
          ? 'flex h-full min-h-0 w-full overflow-y-auto border-r-0'
          : 'sticky top-0 h-svh w-(--shell-sidebar-w) shrink-0 overflow-y-auto md:flex',
        !drawer && (open ? 'flex' : 'hidden'),
      )}
      data-testid={drawer ? 'mobile-sidebar' : 'sidebar'}
    >
      <Link
        to={homeHref}
        aria-label={t('shell:nav.home')}
        className="giyus-brand no-underline"
        onClick={() => {
          selectSidebarMenu('home', homeHref);
        }}
        data-testid={drawer ? 'mobile-sidebar-brand-home-link' : 'sidebar-brand-home-link'}
      >
        <BrandLogo symbol className="size-8 shrink-0" />
        <div className="min-w-0">
          <strong>GIYUS</strong>
          <span className="block text-caption text-muted-foreground">{contextLabel}</span>
        </div>
      </Link>
      <ul className="flex flex-col gap-1">{mainGroups.map(renderGroup)}</ul>
      {settingsGroup && (
        <ul className="mt-auto flex flex-col gap-1 border-t border-border pt-3">
          {renderGroup(settingsGroup)}
        </ul>
      )}
    </nav>
  );
}
